/**
 * ReactiveNutritionSummary Component
 * Automatically calculates and displays nutrition totals from an array of ingredients
 * Updates reactively when ingredients change, are added, removed, or replaced
 */

import React, { useMemo } from "react";
import { OutgoingSearchResult } from "../types";
import { NutritionSummaryCard } from "./NutritionSummaryCard";

interface ReactiveNutritionSummaryProps {
    ingredients: OutgoingSearchResult[];
    title?: string;
    itemLabel?: string;
    variant?: "compact" | "standard" | "detailed";
    className?: string;
    style?: React.CSSProperties;
}

export const ReactiveNutritionSummary: React.FC<
    ReactiveNutritionSummaryProps
> = ({
    ingredients,
    title = "Nutrition Summary",
    itemLabel = "ingredients",
    variant = "standard",
    className,
    style,
}) => {
    // Create a dependency string that changes when any nutrition data changes
    const nutritionDependency = useMemo(() => {
        return ingredients
            .map((ing) => {
                const n = ing.nutritionPreview;
                return `${ing.resultId}-${n?.calories || 0}-${
                    n?.protein || 0
                }-${n?.carbs || 0}-${n?.fat || 0}-${n?.fiber || 0}`;
            })
            .join("|");
    }, [ingredients]);

    // Automatically calculate nutrition totals from ingredients
    // This will recalculate whenever the ingredients array changes or any nutrition data changes
    const nutritionTotals = useMemo(() => {
        return ingredients.reduce(
            (total, ingredient) => {
                const nutrition = ingredient.nutritionPreview;
                return {
                    calories: total.calories + (nutrition?.calories || 0),
                    protein: total.protein + (nutrition?.protein || 0),
                    carbs: total.carbs + (nutrition?.carbs || 0),
                    fat: total.fat + (nutrition?.fat || 0),
                    fiber: total.fiber + (nutrition?.fiber || 0),
                };
            },
            { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0 }
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [nutritionDependency]);

    return (
        <NutritionSummaryCard
            title={title}
            nutritionData={nutritionTotals}
            itemCount={ingredients.length}
            itemLabel={itemLabel}
            variant={variant}
            className={className}
            style={style}
        />
    );
};
